import { Icon } from 'components/Icon';
import { LoadingBounce } from 'components/Loading';
import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { uploadNewPrincipalLogo } from 'utils/principal-service/principal-endpoints';

export interface EditPrincipalLogoProps {
  logoUrl?: string;
  onUploadSuccess?: (logoUrl: string) => void;
}

enum UploadStatuses {
  READY,
  IN_PROGRESS,
  ERROR,
}

/**
 * Renders the principal logo section: preview area, upload trigger, and requirements text.
 */
export const EditPrincipalLogo: React.FC<EditPrincipalLogoProps> = ({
  logoUrl,
  onUploadSuccess,
}) => {
  const { t } = useTranslation('translation');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadStatus, setUploadStatus] = useState(UploadStatuses.READY);

  const handleUploadClick = () => {
    if (uploadStatus !== UploadStatuses.IN_PROGRESS) {
      fileInputRef.current?.click();
    }
  };
  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) {
      return;
    }
    setUploadStatus(UploadStatuses.IN_PROGRESS);
    try {
      const { data } = await uploadNewPrincipalLogo(file);
      onUploadSuccess?.(data.url);
    } catch (err) {
      console.error('Principal logo upload failed', err);
      setUploadStatus(UploadStatuses.ERROR);
    } finally {
      setUploadStatus(UploadStatuses.READY);
    }
  };
  return (
    <>
      <p className="mb-1 font-sans text-base font-semibold leading-6">
        {t(
          'features:admin-center:principalAdministration:editPrincipalModal:logo:title',
        )}
      </p>
      <div className="bg-grey-100 rounded-lg px-4 py-5">
        <button
          type="button"
          onClick={handleUploadClick}
          className="disabled:text-grey-600 mb-5 w-full text-blue-200"
          disabled={uploadStatus === UploadStatuses.IN_PROGRESS}
        >
          <div className="border-grey-600 mx-auto mb-4 flex h-20 w-20 flex-shrink-0 items-center justify-center overflow-hidden rounded border border-dashed">
            {uploadStatus === UploadStatuses.IN_PROGRESS ? (
              <LoadingBounce />
            ) : logoUrl ? (
              <img
                src={logoUrl}
                alt={t(
                  'features:admin-center:principalAdministration:editPrincipalModal:logo:title',
                )}
                className="h-full w-full object-cover"
              />
            ) : null}
          </div>
          <div className="flex items-center justify-center gap-2 text-base font-medium">
            <div className="h-4 w-4">
              <Icon name="Upload" />
            </div>
            <span>
              {t(
                `features:admin-center:principalAdministration:editPrincipalModal:logo:${
                  logoUrl ? 'replace' : 'upload'
                }Cta`,
              )}
            </span>
          </div>
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/png"
          onChange={handleFileChange}
          className="hidden"
          aria-hidden
        />
        {uploadStatus === UploadStatuses.ERROR && (
          <div className="mb-2 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-600">
            {t(
              'features:admin-center:principalAdministration:editPrincipalModal:logo:uploadError',
            )}
          </div>
        )}
        <div className="text-grey-700 text-left text-sm">
          <p className="font-medium">
            {t(
              'features:admin-center:principalAdministration:editPrincipalModal:logo:requirementsHeading',
            )}
          </p>
          <ul className="m-3 mb-5 list-outside list-disc">
            <li>
              {t(
                'features:admin-center:principalAdministration:editPrincipalModal:logo:formatPng',
              )}
            </li>
            <li>
              {t(
                'features:admin-center:principalAdministration:editPrincipalModal:logo:aspectRatioSquare',
              )}
            </li>
            <li>
              {t(
                'features:admin-center:principalAdministration:editPrincipalModal:logo:sizeMax',
              )}
            </li>
          </ul>
        </div>
      </div>
    </>
  );
};
